import { Button, Typography, Stack, Chip, Grid, OutlinedInput, Box, Select, MenuItem } from "@mui/material";
import InsertLinkOutlinedIcon from "@mui/icons-material/InsertLinkOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import { useState } from "react";
import { cacheService, titleService } from "../../../Core/Api/services";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { SearchTitleRequest } from "../../../Core/Api/Types/search-title-request";
import { Title } from "../../../Core/Api/Types/title";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import SelectableCard from "../../Cards/SelectableCard";
import TitleItem from "../../Lists/TitleItemList/TitleItem";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

const ModalEditMetadataDatabase = ({
  title,
  onCloseParent,
}: {
  title: DbAssetObjectInfos;
  onCloseParent?: () => void;
}) => {
  const { t } = useTranslation();
  const [linkedTitles, setLinkedTitles] = useState<Title[]>([]);
  const [titleToLink, setTitleToLink] = useState<number>();
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [isSynced, setIsSynced] = useState<boolean>(false);
  const [isLoadingImportMeta, setIsLoadingImportMeta] = useState<boolean>(false);
  const [isLoadingImportArtwork, setIsLoadingImportArtwork] = useState<boolean>(false);
  const [searchPattern, setSearchPattern] = useState(title.name || "");
  const [selectedSearchMode, setSelectedSearchMode] = useState("pattern");

  const enqueueCustomSnackbar = useCustomSnackbar();

  let linkedId: number | undefined = GetMetadataByTagName(title, "connectId")?.value;

  const onConnectClick = () => {
    setIsSearching(true);

    titleService
      .ConnectSearchTitle(
        new SearchTitleRequest({
          titleId: title.id,
          pattern: selectedSearchMode === "pattern" ? searchPattern : null,
          connectId: selectedSearchMode == "connect-id" ? parseInt(searchPattern) : null,
        })
      )
      .then(function (response) {
        if (!response.success)
          enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
        else {
          if (response.titles) {
            if (response.titles.length > 0) {
              setLinkedTitles(response.titles);

              response.titles?.forEach((element) => {
                if (element.isLinked) setTitleToLink(element.id!);
              });
            } else enqueueCustomSnackbar(t("alert:warning.noMatchFoundIntoConnect"), "warning");
          }
        }
        setIsSearching(false);
      });
  };

  const onLinkClick = () => {
    if (titleToLink)
      titleService.ConnectLinkTitle(title.id, titleToLink).then((response) => {
        if (response.success) enqueueCustomSnackbar(t("alert:success.titleLinkedToConnect"), "success");
        else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
      });
  };

  const onImportMetadataClick = () => {
    if (linkedId) {
      setIsLoadingImportMeta(true);

      titleService.ConnectImportMetadata(title.id).then((response) => {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.metadataSyncedAndImported"), "success");
          onCloseParent && onCloseParent();
        } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");

        setIsLoadingImportMeta(false);
      });
    }
  };

  const onSyncClick = () => {
    setIsSyncing(true);

    if (title.id)
      titleService.SyncTitleWithSpock(title.id).then(function (response) {
        setIsSyncing(false);
        if (response.success) enqueueCustomSnackbar(t("alert:success.metadataSyncedWithThirdPartSystem"), "success");
        else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");

        if (response.success) {
          setIsSynced(true);
          onCloseParent && onCloseParent();
        }
      });
  };

  const onImportArtworkClick = () => {
    if (linkedId) {
      setIsLoadingImportArtwork(true);

      titleService.ConnectImportArtworks(title.id).then((response) => {
        if (response.success) enqueueCustomSnackbar(t("alert:success.artworkImported"), "success");
        else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");

        setIsLoadingImportArtwork(false);
      });
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item md={6}>
        <Typography fontWeight={500} mb={0.5}>
          {linkedId
            ? t("modal:modalEditMetadataDatabase.body.linkedToConnect") + linkedId
            : t("modal:modalEditMetadataDatabase.body.notLinkedToConnect")}
        </Typography>
        <Box mb={1.5} display="flex" flexDirection="row" height="40px">
          <OutlinedInput
            onChange={(e) => setSearchPattern(e.target.value)}
            value={searchPattern}
            placeholder={t("field:placeholder.enterSearchPattern")}
            sx={{ mr: 1.5, height: "100%" }}
            type={selectedSearchMode === "pattern" ? "text" : "number"}
          />
          <Select
            sx={{ height: "100%" }}
            value={selectedSearchMode}
            onChange={(e) => setSelectedSearchMode(e.target.value)}
          >
            <MenuItem value="pattern">{t("common:pattern")}</MenuItem>
            <MenuItem value="connect-id">{t("menu:item.connectId")}</MenuItem>
          </Select>
        </Box>

        <Typography color="text.tier">{t("modal:modalEditMetadataDatabase.body.linkYourTitle")}</Typography>
        <Button
          size="small"
          variant="contained"
          color="primary"
          sx={{ mt: 1.5 }}
          disabled={isSearching || linkedTitles.length > 0}
          onClick={onConnectClick}
          {...{
            startIcon: <InsertLinkOutlinedIcon />,
          }}
        >
          {isSearching ? t("button:searching") : t("button:searchIntoConnect")}
        </Button>
        {UserHasAcl(AclTypes.aclConnectImportTitle) && linkedId && (
          <>
            <Button
              size="small"
              sx={{ mt: 1.5, ml: 1.5 }}
              onClick={onImportMetadataClick}
              loading={isLoadingImportMeta}
            >
              {t("button:importMetadata")}
            </Button>
            <Button
              size="small"
              sx={{ mt: 1.5, ml: 1.5 }}
              onClick={onImportArtworkClick}
              loading={isLoadingImportArtwork}
            >
              {t("button:importArtwork")}
            </Button>
          </>
        )}
        {linkedTitles.length > 0 && (
          <>
            <Typography color="primary.text" fontWeight={500} mb={0.5} mt={1.5}>
              {t("common:resultFound", { count: linkedTitles.length })}
            </Typography>
            <Typography variant="body2" color="text.tier">
              {t("modal:body.selectBestResultToLinkToTitle", { titleName: title.name })}
            </Typography>
            <Stack spacing={1} mt={1.5}>
              {linkedTitles.map((data: Title) => (
                <SelectableCard
                  selected={titleToLink === data.id}
                  onClick={() => setTitleToLink(data.id!)}
                  key={data.id}
                >
                  <TitleItem poster={"data:image/jpeg;base64," + data.thumbnail!} size="small" title={data.name!}>
                    <Stack direction="row" spacing={1}>
                      {data.languageName && <Chip size="small" label={data.languageName} />}
                      {data.productionYear && <Chip size="small" label={data.productionYear} />}
                      {data.territories?.length && (
                        <Chip size="small" label={t("field:label.territories", { count: data.territories?.length })} />
                      )}
                      {/* Check below to see if we could include the whole string into translation file */}
                      {data.id && <Chip size="small" label={`${t("common:connect")} #${data.id}`} color="info" />}
                    </Stack>
                  </TitleItem>
                </SelectableCard>
              ))}
            </Stack>
            <Stack direction="row" spacing={1.5} mt={1.5}>
              <Button onClick={() => setLinkedTitles([])}>{t("common:cancel")}</Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={<InsertLinkOutlinedIcon />}
                onClick={() => onLinkClick()}
                disabled={
                  titleToLink === null || titleToLink === undefined || !UserHasAcl(AclTypes.aclConnectLinkTitle)
                }
              >
                {t("button:linkTitle")}
              </Button>
            </Stack>
          </>
        )}
      </Grid>
      {UserHasAcl(AclTypes.aclSyncTitleFromSpock) && cacheService.GetSpockAgentType() && (
        <Grid item md={6}>
          {isSynced ? (
            <Stack fontWeight={500} mb={0.5} direction="row" spacing={1} alignItems="center" color="success.main">
              <span>{t("modal:body.freshlySyncedTo", { agentType: cacheService.GetSpockAgentType() })}</span>
              <CheckOutlinedIcon fontSize="small" />
            </Stack>
          ) : (
            <Stack fontWeight={500} mb={0.5} direction="row" spacing={1} alignItems="center">
              <span>{t("modal:body.linkedToAgentType", { agentType: cacheService.GetSpockAgentType() })}</span>
            </Stack>
          )}
          <Typography color="text.tier">
            {t("modal:body.yourPlatformUsesAgentTypeAsThirdPartyDatabase", {
              agentType: cacheService.GetSpockAgentType(),
            })}
          </Typography>
          <Button
            size="small"
            startIcon={<SyncOutlinedIcon />}
            sx={{ mt: 1.5 }}
            onClick={onSyncClick}
            disabled={isSynced || isSyncing}
          >
            {isSyncing ? t("button:syncingMetadata") : t("button:syncMetadata")}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};
export default ModalEditMetadataDatabase;
